import { ComponentProps, useEffect } from 'react'
import {
  AutocompleteMultiselection,
  YesObject
} from '@yes.technology/react-toolkit'
import useSuggestionsOnChange from 'site/Renderer/components/FilterFields/hooks/useSuggestionsOnChange'
import SelectAndLinkButton from 'site/Renderer/components/DataTables/components/TableActions/SelectAndLinkButton'
import { RelationAutocompleteContainer } from './RelationAutocomplete.styles'
import SectionDivider from 'site/Renderer/components/SectionDivider/SectionDivider'
import { useTranslation } from 'react-i18n-lite'
import MultiselectionRelation from './MultiselectionRelation'
import UniselectionRelation from './UniselectionRelation'
import { RelationAutocompleteProps } from './RelationAutocomplete.types'

type Option = ComponentProps<typeof AutocompleteMultiselection>['options'][0]

type GetOptionsParams = {
  selectedObjects: YesObject[]
  suggestions: YesObject[]
}

const getOptions = ({
  selectedObjects,
  suggestions
}: GetOptionsParams): { selectedOptions: Option[]; options: Option[] } => {
  const mappedSuggestions = suggestions.map((suggestion) => {
    return {
      id: suggestion.uuid,
      value: suggestion.des
    }
  })

  const selectedOptions = selectedObjects.map(({ uuid, des }) => ({
    id: uuid,
    value: des
  }))

  const uniqueSelectedOptions = selectedOptions.filter(
    (selectedOption) =>
      !suggestions.find((suggestion) => suggestion.uuid === selectedOption.id)
  )

  const options: Option[] = [...uniqueSelectedOptions, ...mappedSuggestions]

  return { options, selectedOptions }
}

const RelationAutocomplete = ({
  inputRef,
  objectclassUuid,
  onChange,
  selectedObjects,
  informational,
  selectAndLinkOptions,
  selectAndLinkTargetPath,
  hideSelectAndLinkAction,
  title,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  multiplicity,
  id
}: RelationAutocompleteProps) => {
  const isUniselectionRelation =
    multiplicity === '1:1' || multiplicity === 'N:1'

  const {
    value,
    onChange: handleChange,
    isLoading,
    suggestions,
    resetValue
  } = useSuggestionsOnChange({
    initialValue: isUniselectionRelation
      ? selectedObjects.at(-1)?.des
      : undefined,
    objectclassUuid,
    skipMetadata: false,
    skipRelations: false
  })

  useEffect(() => {
    if (selectedObjects.length === 0) {
      resetValue()
    }
  }, [selectedObjects, resetValue])

  const { options, selectedOptions } = getOptions({
    selectedObjects,
    suggestions
  })

  const { t } = useTranslation()
  const label = t('object-view.description')

  const statusClass = isLoading ? 'processing' : 'default'

  return (
    <SectionDivider
      id={id}
      title={title}
      isCollapsibleCard={false}
      backgroundColor={navigationSliderBackgroundColor}
      color={navigationSliderColor}
      isVisible
      hideCollapsibleCardArrow
    >
      <RelationAutocompleteContainer>
        {isUniselectionRelation ? (
          <UniselectionRelation
            onChangeObjects={onChange}
            onChangeValue={handleChange}
            {...{
              inputRef,
              label,
              options,
              statusClass,
              suggestions,
              value,
              informational
            }}
          />
        ) : (
          <MultiselectionRelation
            onChangeObjects={onChange}
            onChangeValue={handleChange}
            {...{
              inputRef,
              label,
              options,
              statusClass,
              suggestions,
              selectedObjects,
              selectedOptions,
              value,
              informational
            }}
          />
        )}

        {!informational && (
          <SelectAndLinkButton
            variant='icon'
            {...{
              objectclassUuid,
              selectAndLinkOptions,
              selectAndLinkTargetPath,
              hideSelectAndLinkAction
            }}
          />
        )}
      </RelationAutocompleteContainer>
    </SectionDivider>
  )
}

export default RelationAutocomplete
